/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router";

const MAX_STACK_SIZE = 50;

// Origin types
const NavigationOrigin = {
  DASHBOARD: "dashboard",
  CATALOG: "catalog",
  UNKNOWN: "unknown",
};

// Routes that act as navigation reset points
const RESET_ROUTES = ["/", "/catalog"];

function isResetRoute(path) {
  const pathWithoutSearch = path.split("?")[0];
  return RESET_ROUTES.some(route => {
    if (route === "/") {
      return pathWithoutSearch === "/" || pathWithoutSearch === "";
    }
    return pathWithoutSearch === route || pathWithoutSearch.startsWith(`${route}/`);
  });
}

function isPathRoute(path) {
  const pathWithoutSearch = path.split("?")[0];
  return /^\/path\/[^/]+\/?$/.test(pathWithoutSearch);
}

function isDashboardRoute(path) {
  const pathWithoutSearch = path.split("?")[0];
  return pathWithoutSearch === "/" || pathWithoutSearch === "";
}

function isCatalogRoute(path) {
  const pathWithoutSearch = path.split("?")[0];
  return pathWithoutSearch === "/catalog" || pathWithoutSearch.startsWith("/catalog/");
}

// Create context
const LearnNavigationContext = createContext(null);

// Provider component
export function LearnNavigationProvider({ children }) {
  const location = useLocation();
  const [stack, setStack] = useState([]);

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    const pathOnly = location.pathname;

    setStack(prevStack => {
      // If navigating to a reset route, clear the stack
      if (isResetRoute(pathOnly)) {
        return [currentPath];
      }

      // If navigating to a Path page, trim stack back to last reset point + this path
      if (isPathRoute(pathOnly)) {
        const resetIndex = prevStack.findIndex(p => isResetRoute(p.split("?")[0]));

        if (resetIndex !== -1) {
          return [...prevStack.slice(0, resetIndex + 1), currentPath];
        }
        return [currentPath];
      }

      // Normal navigation: avoid duplicate consecutive entries
      const lastEntry = prevStack[prevStack.length - 1];
      if (lastEntry === currentPath) {
        return prevStack;
      }

      const newStack = [...prevStack, currentPath];
      if (newStack.length > MAX_STACK_SIZE) {
        return newStack.slice(-MAX_STACK_SIZE);
      }
      return newStack;
    });
  }, [location]);

  const getPreviousPage = useCallback(() => {
    return stack.length > 1 ? stack[stack.length - 2] : null;
  }, [stack]);

  const getStack = useCallback(() => {
    return [...stack];
  }, [stack]);

  const popPage = useCallback(() => {
    if (stack.length > 1) {
      const newStack = stack.slice(0, -1);
      setStack(newStack);
      return newStack[newStack.length - 1];
    }
    return null;
  }, [stack]);

  const clearStack = useCallback(() => {
    setStack([]);
  }, []);

  const getOrigin = useCallback(() => {
    for (const path of stack) {
      const pathOnly = path.split("?")[0];
      if (isCatalogRoute(pathOnly)) {
        return NavigationOrigin.CATALOG;
      }
      if (isDashboardRoute(pathOnly)) {
        return NavigationOrigin.DASHBOARD;
      }
    }
    return NavigationOrigin.UNKNOWN;
  }, [stack]);

  const isFromDashboard = useCallback(() => {
    return getOrigin() === NavigationOrigin.DASHBOARD;
  }, [getOrigin]);

  const isFromCatalog = useCallback(() => {
    return getOrigin() === NavigationOrigin.CATALOG;
  }, [getOrigin]);

  const value = {
    getPreviousPage,
    getStack,
    popPage,
    clearStack,
    getOrigin,
    isFromDashboard,
    isFromCatalog,
    currentPage: location.pathname + location.search,
  };

  return (
    <LearnNavigationContext.Provider value={value}>{children}</LearnNavigationContext.Provider>
  );
}

// Hook to use the navigation stack
export function useLearnNavigationStack() {
  const context = useContext(LearnNavigationContext);
  if (!context) {
    throw new Error("useLearnNavigationStack must be used within a LearnNavigationProvider");
  }
  return context;
}
